import type { PageSchemaField, Searchability } from '@shopexperts/shared';
import { PAGE_FIELD_TYPES, validateSchemaFields } from '../field-types';

/** Everything about a schema that the builder is allowed to change. */
export interface SchemaDraft {
  name: string;
  label: string;
  description: string;
  searchability: Searchability;
  group_label: string;
  group_view_all_label: string;
  group_view_all_href: string;
  search_group_sort_index: number | null;
  fields: PageSchemaField[];
}

export interface ParsedSchemaDraft {
  draft?: SchemaDraft;
  errors: string[];
}

const FIELD_TYPES = new Set(PAGE_FIELD_TYPES.map(definition => definition.type));
const SEARCHABILITIES: Searchability[] = ['searchable', 'non-searchable'];

/**
 * Serialized with a fixed key order so the JSON view stays diffable between
 * edits instead of reshuffling whenever the draft is re-serialized.
 */
export function serializeSchemaDraft(draft: SchemaDraft): string {
  return `${JSON.stringify({
    name: draft.name,
    label: draft.label,
    description: draft.description,
    searchability: draft.searchability,
    group_label: draft.group_label,
    group_view_all_label: draft.group_view_all_label,
    group_view_all_href: draft.group_view_all_href,
    search_group_sort_index: draft.search_group_sort_index,
    fields: draft.fields.map(serializeField),
  }, null, 2)}\n`;
}

export function parseSchemaDraft(text: string): ParsedSchemaDraft {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (error) {
    return { errors: [`Invalid JSON: ${error instanceof Error ? error.message : 'could not be parsed'}`] };
  }

  if (!isRecord(parsed)) return { errors: ['The schema must be a JSON object.'] };

  const errors: string[] = [];
  const name = readString(parsed.name, 'name', errors, { required: true });
  const label = readString(parsed.label, 'label', errors, { required: true });
  const description = readString(parsed.description, 'description', errors);
  const groupLabel = readString(parsed.group_label, 'group_label', errors);
  const groupViewAllLabel = readString(parsed.group_view_all_label, 'group_view_all_label', errors);
  const groupViewAllHref = readString(parsed.group_view_all_href, 'group_view_all_href', errors);

  let searchability: Searchability = 'non-searchable';
  if (parsed.searchability !== undefined && parsed.searchability !== null) {
    if (SEARCHABILITIES.includes(parsed.searchability as Searchability)) {
      searchability = parsed.searchability as Searchability;
    } else {
      errors.push(`"searchability" must be one of ${SEARCHABILITIES.map(value => `"${value}"`).join(' or ')}.`);
    }
  }

  let sortIndex: number | null = null;
  if (parsed.search_group_sort_index !== undefined && parsed.search_group_sort_index !== null) {
    if (typeof parsed.search_group_sort_index === 'number' && Number.isFinite(parsed.search_group_sort_index)) {
      sortIndex = parsed.search_group_sort_index;
    } else {
      errors.push('"search_group_sort_index" must be a number or null.');
    }
  }

  const fields = parseFields(parsed.fields, errors);
  errors.push(...validateSchemaFields(fields));

  if (errors.length) return { errors };

  return {
    draft: {
      name,
      label,
      description,
      searchability,
      group_label: groupLabel,
      group_view_all_label: groupViewAllLabel,
      group_view_all_href: groupViewAllHref,
      search_group_sort_index: sortIndex,
      fields,
    },
    errors: [],
  };
}

function serializeField(field: PageSchemaField) {
  return {
    key: field.key,
    label: field.label,
    type: field.type,
    required: Boolean(field.required),
    default_value: field.default_value ?? null,
    options: [...(field.options || [])],
    ...(field.search_role ? { search_role: [...field.search_role] } : {}),
    ...(field.search_role_priority !== undefined ? { search_role_priority: field.search_role_priority } : {}),
  };
}

function parseFields(value: unknown, errors: string[]): PageSchemaField[] {
  if (value === undefined || value === null) {
    errors.push('"fields" is required and must be an array.');
    return [];
  }
  if (!Array.isArray(value)) {
    errors.push('"fields" must be an array.');
    return [];
  }

  const fields: PageSchemaField[] = [];
  value.forEach((entry, index) => {
    const position = `Field ${index + 1}`;
    if (!isRecord(entry)) {
      errors.push(`${position} must be an object.`);
      return;
    }
    if (typeof entry.key !== 'string') {
      errors.push(`${position} needs a "key" string.`);
      return;
    }
    if (typeof entry.label !== 'string') {
      errors.push(`${position} needs a "label" string.`);
      return;
    }
    if (typeof entry.type !== 'string' || !FIELD_TYPES.has(entry.type as PageSchemaField['type'])) {
      errors.push(`${position} has an unknown type "${String(entry.type)}". Known types: ${[...FIELD_TYPES].join(', ')}.`);
      return;
    }
    if (entry.options !== undefined && entry.options !== null && !isStringArray(entry.options)) {
      errors.push(`${position} "options" must be an array of strings.`);
      return;
    }

    fields.push({
      key: entry.key,
      label: entry.label,
      type: entry.type as PageSchemaField['type'],
      required: Boolean(entry.required),
      default_value: entry.default_value ?? null,
      options: isStringArray(entry.options) ? [...entry.options] : [],
      ...(isStringArray(entry.search_role) ? { search_role: entry.search_role as PageSchemaField['search_role'] } : {}),
      ...(typeof entry.search_role_priority === 'number' ? { search_role_priority: entry.search_role_priority } : {}),
    });
  });

  return fields;
}

function readString(value: unknown, key: string, errors: string[], options: { required?: boolean } = {}): string {
  if (value === undefined || value === null) {
    if (options.required) errors.push(`"${key}" is required.`);
    return '';
  }
  if (typeof value !== 'string') {
    errors.push(`"${key}" must be a string.`);
    return '';
  }
  if (options.required && !value.trim()) errors.push(`"${key}" is required.`);
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(entry => typeof entry === 'string');
}
